import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import HoneycombLogo from '../components/HoneycombLogo';
import './LoginPage.css';

const EXPIRY_MS = 10 * 60 * 1000; // 10 minutes
const MAX_ATTEMPTS = 3;

function generateOtp() {
    return String(Math.floor(100000 + Math.random() * 900000));
}

const ForgotPasswordPage = () => {
    const navigate = useNavigate();
    const [step, setStep] = useState(1); // 1=email, 2=otp, 3=newpass, 4=done
    const [email, setEmail] = useState('');
    const [otpInput, setOtpInput] = useState('');
    const [generatedOtp, setGeneratedOtp] = useState('');
    const [otpExpiry, setOtpExpiry] = useState(null);
    const [otpAttempts, setOtpAttempts] = useState(0);
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const handleSendOtp = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        try {
            const { data: lookupData, error: lookupErr } = await supabase.functions.invoke('firebase-admin', {
                body: { action: 'getUserByEmail', email: email.trim().toLowerCase() },
            });
            if (lookupErr) throw new Error('Could not verify email. Please try again.');
            if (!lookupData?.uid) {
                setError('No admin account found with this email address.');
                setLoading(false);
                return;
            }
            const otp = generateOtp();
            setGeneratedOtp(otp);
            setOtpExpiry(Date.now() + EXPIRY_MS);
            setOtpAttempts(0);
            const { error: emailErr } = await supabase.functions.invoke('send-email', {
                body: {
                    template: 'password-reset-otp',
                    to: email.trim().toLowerCase(),
                    data: { otp, expiryMinutes: '10', portalType: 'admin' },
                },
            });
            if (emailErr) throw new Error('Failed to send OTP email. Please try again.');
            setStep(2);
        } catch (err) {
            setError(err.message || 'Something went wrong. Please try again.');
        }
        setLoading(false);
    };

    const handleVerifyOtp = (e) => {
        e.preventDefault();
        setError('');
        if (Date.now() > otpExpiry) {
            setError('OTP has expired. Please request a new one.');
            setStep(1);
            return;
        }
        if (otpAttempts >= MAX_ATTEMPTS) {
            setError('Too many incorrect attempts. Please request a new OTP.');
            setStep(1);
            return;
        }
        if (otpInput.trim() !== generatedOtp) {
            const next = otpAttempts + 1;
            setOtpAttempts(next);
            if (next >= MAX_ATTEMPTS) {
                setError('Too many incorrect attempts. Please request a new OTP.');
                setStep(1);
            } else {
                setError(`Incorrect OTP. ${MAX_ATTEMPTS - next} attempt(s) remaining.`);
            }
            return;
        }
        setStep(3);
    };

    const handleResetPassword = async (e) => {
        e.preventDefault();
        setError('');
        if (newPassword.length < 6) { setError('Password must be at least 6 characters.'); return; }
        if (newPassword !== confirmPassword) { setError('Passwords do not match.'); return; }
        setLoading(true);
        try {
            const { data: lookupData, error: lookupErr } = await supabase.functions.invoke('firebase-admin', {
                body: { action: 'getUserByEmail', email: email.trim().toLowerCase() },
            });
            if (lookupErr || !lookupData?.uid) throw new Error('Could not find your account. Please try again.');
            const { error: updateErr } = await supabase.functions.invoke('firebase-admin', {
                body: { action: 'updateUser', uid: lookupData.uid, password: newPassword },
            });
            if (updateErr) throw new Error('Failed to update password. Please try again.');
            setStep(4);
        } catch (err) {
            setError(err.message || 'Something went wrong. Please try again.');
        }
        setLoading(false);
    };

    const stepTitle = ['', 'Reset Password', 'Enter OTP', 'New Password', 'Password Reset!'][step];
    const stepDesc = [
        '',
        "Enter your admin account email and we'll send you a one-time code.",
        `A 6-digit OTP was sent to ${email}. Check your inbox (and spam folder).`,
        'OTP verified! Choose a strong new password.',
        'Your password has been updated. You can now log in.',
    ][step];

    return (
        <div className="auth-page">
            <div className="auth-container">
                <div className="auth-header">
                    <HoneycombLogo size={60} />
                    <h1>{stepTitle}</h1>
                    <p>{stepDesc}</p>
                </div>

                {error && <div className="error-message">{error}</div>}

                {step === 1 && (
                    <form onSubmit={handleSendOtp} className="auth-form">
                        <div className="form-group">
                            <label htmlFor="email">Email</label>
                            <input
                                type="email"
                                id="email"
                                value={email}
                                onChange={e => setEmail(e.target.value)}
                                required
                                placeholder="admin@skillhive.com"
                                autoFocus
                            />
                        </div>
                        <button type="submit" className="btn btn-primary btn-full" disabled={loading}>
                            {loading ? 'Sending OTP…' : 'Send OTP'}
                        </button>
                    </form>
                )}

                {step === 2 && (
                    <form onSubmit={handleVerifyOtp} className="auth-form">
                        <div className="form-group">
                            <label htmlFor="otp">6-Digit OTP</label>
                            <input
                                type="text"
                                id="otp"
                                value={otpInput}
                                onChange={e => setOtpInput(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                required
                                placeholder="Enter 6-digit code"
                                maxLength={6}
                                autoFocus
                                style={{ letterSpacing: '0.3em', fontSize: '1.4rem', textAlign: 'center' }}
                            />
                        </div>
                        <button type="submit" className="btn btn-primary btn-full">
                            Verify OTP
                        </button>
                        <div style={{ textAlign: 'center', marginTop: '12px' }}>
                            <button type="button"
                                style={{ background: 'none', border: 'none', color: '#facc15', fontWeight: 600, cursor: 'pointer', fontSize: '14px' }}
                                onClick={() => { setStep(1); setOtpInput(''); setError(''); }}>
                                Resend OTP
                            </button>
                        </div>
                    </form>
                )}

                {step === 3 && (
                    <form onSubmit={handleResetPassword} className="auth-form">
                        <div className="form-group">
                            <label htmlFor="newPassword">New Password</label>
                            <input
                                type="password"
                                id="newPassword"
                                value={newPassword}
                                onChange={e => setNewPassword(e.target.value)}
                                required
                                placeholder="Min. 6 characters"
                                minLength={6}
                                autoFocus
                            />
                        </div>
                        <div className="form-group">
                            <label htmlFor="confirmPassword">Confirm Password</label>
                            <input
                                type="password"
                                id="confirmPassword"
                                value={confirmPassword}
                                onChange={e => setConfirmPassword(e.target.value)}
                                required
                                placeholder="Re-enter new password"
                            />
                        </div>
                        <button type="submit" className="btn btn-primary btn-full" disabled={loading}>
                            {loading ? 'Updating…' : 'Set New Password'}
                        </button>
                    </form>
                )}

                {step === 4 && (
                    <div style={{ textAlign: 'center', padding: '12px 0' }}>
                        <div style={{ fontSize: '3rem', marginBottom: '12px' }}>✅</div>
                        <p style={{ marginBottom: '20px', color: '#9ca3af' }}>
                            Your admin password has been reset. You can now log in with your new password.
                        </p>
                        <button className="btn btn-primary btn-full" onClick={() => navigate('/login')}>
                            Go to Login
                        </button>
                    </div>
                )}

                {step < 4 && (
                    <div className="auth-footer" style={{ textAlign: 'center', marginTop: '16px' }}>
                        <Link to="/login" style={{ color: '#facc15', fontWeight: 600, textDecoration: 'none', fontSize: '14px' }}>
                            ← Back to Login
                        </Link>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ForgotPasswordPage;
